import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './components/ThemeContext';
import StructuredData from './components/StructuredData';
import { getActiveBrand } from './utils/brandRouting';
import './index.css';

// TickleApps pages. The parent site is deliberately small: home, products,
// about, support, and the parent legal routes. Anything not declared here
// (including the retired /blog, /social, /vision, /mission and global /faq
// routes) falls through to NotFound.
import Home     from './pages/Home';
import About    from './pages/About';
import Apps     from './pages/Apps';
import Contact  from './pages/Contact';
import Privacy  from './pages/Privacy';
import DPDPA    from './pages/DPDPA';
import NotFound from './pages/NotFound';

// NutriScan pages
import NutriScan  from './pages/ns/NutriScan';
import NSPrivacy  from './pages/ns/NSPrivacy';
import NSTerms    from './pages/ns/NSTerms';
import NSFAQ      from './pages/ns/NSFAQ';
import NSDeletion from './pages/ns/NSDeletion';

// CardCycle pages
import CardCycle  from './pages/cc/CardCycle';
import CCPrivacy  from './pages/cc/CCPrivacy';
import CCTerms    from './pages/cc/CCTerms';
import CCFAQ      from './pages/cc/CCFAQ';
import CCDeletion from './pages/cc/CCDeletion';

// Dual-mount route tables: on a sub-brand's own subdomain the app is
// mounted at "/" instead of "/apps/<brand>" — see utils/brandRouting.js.
// Deep-link/asset-path fix (white-screen on nested URLs) lives at the build
// level (absolute /static/... asset paths, see docs/build-and-deploy.md) —
// this file only decides which route table applies to the current host.
function NutriScanRoutes() {
  return (
    <Routes>
      <Route path="/"        element={<NutriScan />} />
      <Route path="/privacy" element={<NSPrivacy />} />
      <Route path="/terms"   element={<NSTerms />} />
      <Route path="/faq"     element={<NSFAQ />} />
      <Route path="/account-deletion" element={<NSDeletion />} />
      <Route path="*"        element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function CardCycleRoutes() {
  return (
    <Routes>
      <Route path="/"        element={<CardCycle />} />
      <Route path="/privacy" element={<CCPrivacy />} />
      <Route path="/terms"   element={<CCTerms />} />
      <Route path="/faq"     element={<CCFAQ />} />
      <Route path="/account-deletion" element={<CCDeletion />} />
      <Route path="*"        element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function MainSiteRoutes() {
  return (
    <Routes>
      <Route path="/"                       element={<Home />} />
      <Route path="/about"                  element={<About />} />
      <Route path="/apps"                   element={<Apps />} />
      <Route path="/apps/nutriscan"         element={<NutriScan />} />
      <Route path="/apps/nutriscan/privacy" element={<NSPrivacy />} />
      <Route path="/apps/nutriscan/terms"   element={<NSTerms />} />
      <Route path="/apps/nutriscan/faq"     element={<NSFAQ />} />
      <Route path="/apps/nutriscan/account-deletion" element={<NSDeletion />} />
      <Route path="/apps/cardcycle"         element={<CardCycle />} />
      <Route path="/apps/cardcycle/privacy" element={<CCPrivacy />} />
      <Route path="/apps/cardcycle/terms"   element={<CCTerms />} />
      <Route path="/apps/cardcycle/faq"     element={<CCFAQ />} />
      <Route path="/apps/cardcycle/account-deletion" element={<CCDeletion />} />
      <Route path="/contact"                element={<Contact />} />
      <Route path="/privacy"                element={<Privacy />} />
      <Route path="/dpdpa"                  element={<DPDPA />} />
      <Route path="*"                       element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  const activeBrand = getActiveBrand();
  const RouteTable = activeBrand === 'nutriscan' ? NutriScanRoutes
    : activeBrand === 'cardcycle' ? CardCycleRoutes
    : MainSiteRoutes;

  return (
    <ThemeProvider>
      {/* One Organization/WebSite JSON-LD block for the whole site (T06). */}
      <StructuredData />
      <BrowserRouter>
        <RouteTable />
      </BrowserRouter>
    </ThemeProvider>
  );
}
